import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { fetchBackend } from '../../backend-variables/address';
import { useAppDispatch, useAppSelector } from '../../hooks/redux-hooks';
import { viewingActions } from '../../store/viewing-slice';
import { AbbreviatedItem, CatType, Item, MaintenanceMethod, NumericRange, SupplierSummary } from '../../types/item_types';
import { Sector } from '../../types/sector_types';
import BigButton from '../UI/BigButton';
import classes from './ItemMenu.module.css';
import ItemDetails from './ItemDetails';
import DeviceFields from './DeviceFields';
import AccessoryFields from './AccessoryFields';
import ConsumableFields from './ConsumableFields';
import SparePartFields from './SparePartFields';
import { Role } from '../../types/user_types';
import { MdRemoveCircle } from 'react-icons/md';
import LabeledInput from '../UI/LabeledInput';
import { useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';

const notifyPartiallyLocked = () => toast('חלק מהפריטים נמצאים בעריכה ע"י משתמש אחר והושמטו מהעריכה במסך זה', { id: 'lock'} );

function vacateItemListIfEmptyAndRemoveSpaces(itemList: AbbreviatedItem[]) {
    const filteredList = itemList.filter(i => i.cat !== "" || i.name !== "");
    return filteredList.map(i => {
        const output = {...i};
        output.cat = output.cat.replace(/ /g, '');
        return output;
    })
}

const allowedFields = [
    { names: [ 'sector', 'department' ], text: 'מדור ותחום'},
    { names: [ 'supplier' ], text: 'ספק' },
    { names: [ 'emergency' ], text: 'חירום', exceptCatTypes: [ CatType.Accessory, CatType.Consumable, CatType.SparePart ] },
    { names: [ 'belongsToDevices' ], text: 'שייך למכשירים', exceptCatTypes: [ CatType.Device ] }
];

const MultiItemEdit = () => {
    const { jwt: authToken, frontEndPrivilege } = useAppSelector(state => state.auth);
    const { searchVal, sector: filteredSector, department: filteredDepartment, showArchived } = useAppSelector(state => state.viewing.searching);
    const { selectAllItems, selectedItems, excludedItems } = useAppSelector(state => state.viewing.itemManagement);
    const items = useAppSelector(state => state.items.items);
    const [sectorsToChooseFrom, setSectorsToChooseFrom] = useState<Sector[]>([]);
    const dispatch = useAppDispatch();
    const [sector, setSector] = useState("");
    const [department, setDepartment] = useState("");
    const [emergency, setEmergency] = useState(false);
    const [maintenanceMethod, setMaintenanceMethod] = useState(MaintenanceMethod.PeriodicTestAndCalibration);
    const [maintenanceIntervalMonths, setMaintenanceIntervalMonths] = useState<number | null>(null);
    const [storageTemperature, setStorageTemperature] = useState<NumericRange | null>(null);
    const [storageHumidity, setStorageHumidity] = useState<NumericRange | null>(null);
    const [supplier, setSupplier] =  useState(undefined as SupplierSummary | null | undefined);
    const [belongsToDevices, setBelongsToDevices] = useState<AbbreviatedItem[]>([{ cat: "", name: "" }]);
    const [fields, setFields ] = useState([] as string[]);
    const navigate = useNavigate();
    const [showLockError, setShowLockError] = useState(false);
    //const [itemsLockId, setItemsLockId] = useState<string | null>(null);

    const lockPromiseRef = useRef<Promise<void> | null>(null);
    const lockIdRef = useRef<string | null>(null);
    const unlockPromiseRef = useRef<Promise<void> | null>(null);

    const createFilterSearchParams = useCallback(() => {
        let searchParams: URLSearchParams;
        if (selectAllItems) {
            searchParams = new URLSearchParams({ selectAll: 'true' });
            if (searchVal) searchParams.append('search', searchVal);
            if (filteredSector) searchParams.append('sector', filteredSector);
            if (filteredDepartment) searchParams.append('department', filteredDepartment);  
            if (!showArchived) searchParams.append('status', 'active');
            excludedItems?.forEach(item => searchParams.append('excludedCats', item.cat!));
        } else {
            searchParams = new URLSearchParams();
            selectedItems?.forEach(item => searchParams.append('cats', item.cat!));
        }
        return searchParams;
    }, [ selectAllItems, searchVal, filteredSector, filteredDepartment, showArchived, selectedItems, excludedItems ] );

    useEffect(() => {
        const lockItems = async () => {
            lockPromiseRef.current = new Promise<void>(async resolve => {
                const promises = [lockPromiseRef.current, unlockPromiseRef.current].filter(Boolean);
                if (promises.length) {
                    await Promise.all([lockPromiseRef.current, unlockPromiseRef.current].filter(Boolean));
                }
                const filter = createFilterSearchParams();
                try {
                    const response = await fetchBackend('items/lock?' + filter.toString(), { 
                        method: 'POST',
                        headers: { 'auth-token': authToken }
                    });
                    if (response.ok) {
                        const json = await response.json();
                        console.log(`items lock result: ${JSON.stringify(json)}`);
                        lockIdRef.current = json.id;
                        if (response.status === 207) {
                            notifyPartiallyLocked();
                        }
                    } else {
                        setShowLockError(true);
                    }
                } catch (error) {
                    setShowLockError(true);
                } finally {
                    resolve();
                }
            });
            await lockPromiseRef.current;
            lockPromiseRef.current = null;
        };

        lockItems();

        return () => {
            const unlockItems = async () => {
                unlockPromiseRef.current = new Promise<void>(async resolve => {
                    console.log(`unlocking items`);
                    if (lockPromiseRef.current) {
                        console.log(`unlock - waiting for lock promise to resolve before unlocking...`);
                        await lockPromiseRef.current;
                        console.log(`unlock - finished waiting for lock promise to resolve before unlocking...`);
                    }
                    console.log(`unlock - finished waiting for lock promise to resolve before unlocking...`);
                    if (lockIdRef.current) {
                        console.log(`unlocking items with lock id: ${lockIdRef.current}`);
                        await fetchBackend(`items/unlock/${lockIdRef.current}`, { 
                            method: 'POST',
                            headers: { 'auth-token': authToken }
                        });   
                    } else {
                        console.log(`no lock id found, skipping unlock request`);
                    } 

                    resolve();
                });

                await unlockPromiseRef.current;
                unlockPromiseRef.current = null;
            };
            unlockItems();
        };
    }, [ createFilterSearchParams, authToken ]);    


    useEffect(() => {
        const getSectors = async () => {
            const params: any = {};
            if (frontEndPrivilege === Role.Technician) {
                params.isMaintenance = true;
            }
            const searchParams = new URLSearchParams(params);
            const fetchedSectors = await fetchBackend(`sectors?` + searchParams, {
                headers: { 'auth-token': authToken }
            });
            return await fetchedSectors.json();
        };

        getSectors().then(s => setSectorsToChooseFrom(s));
    }, [authToken, frontEndPrivilege]);

    const handleInput = (setFunc: ((val: string) => any) | undefined, event: ChangeEvent<HTMLInputElement>) => {
        if (setFunc) {
            setFunc(event.target.value);
            dispatch(viewingActions.changesAppliedToItem(true));
        }
    }
    const handleSetSector = (value: string) => {
        setSector(value);
        setDepartment("");
        dispatch(viewingActions.changesAppliedToItem(true));
    }
    const handleSetDepartment = (value: string) => {
        setDepartment(value);
        dispatch(viewingActions.changesAppliedToItem(true));
    }

    const handleSave = async (): Promise<any> => {
        const data: Record<string, any> = { sector, department, emergency, supplier, belongsToDevices };
        if (fields.some(f => data[f] === undefined || data[f] === "" || (Array.isArray(data[f]) && data[f].length === 0))) {
            console.log(`missing field: ${fields.find(f => data[f] === undefined || data[f] === "" || (Array.isArray(data[f]) && data[f].length === 0))}`);
            // if the required fields of the Item mongo schema are not filled then don't save
            alert("לא כל השדות שנבחרו מולאו");
            return;
        }

        const fullItemDetails: Record<string, any> = {
            sector: sector,
            department: department,
            emergency: emergency,
            supplier: supplier,
            belongsToDevices: vacateItemListIfEmptyAndRemoveSpaces(belongsToDevices)
        };

        const itemDetails = fields.reduce((obj, field) => ({ ...obj, [field]: fullItemDetails[field] }), {})

        let searchParams: URLSearchParams;
        if (selectAllItems) {
            searchParams = new URLSearchParams({ selectAll: 'true' });
            if (searchVal) searchParams.append('search', searchVal);
            if (filteredSector) searchParams.append('sector', filteredSector);
            if (filteredDepartment) searchParams.append('department', filteredDepartment);  
            if (!showArchived) searchParams.append('status', 'active');
            excludedItems?.forEach(item => searchParams.append('excludedCats', item.cat!));
        } else {
            searchParams = new URLSearchParams();
            selectedItems?.forEach(item => searchParams.append('cats', item.cat!));
        }

        try {
            const res = await fetchBackend('items?' + searchParams.toString(), {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'auth-token': authToken
                },
                body: JSON.stringify(itemDetails)
            });

            if (res.status !== 200) {
                const errJson = await res.json();
                alert(`עריכת הפריטים נכשל: ${errJson.details}`);
            }

            dispatch(viewingActions.changesAppliedToItem(false));
            dispatch(viewingActions.changeSelectedItems({ selectAll: false, excludedItems: [], selectedItems: [] }));
            navigate(-1);

            console.log("success saving items");
            
        } catch(err) {
            console.log(`Error saving items: ${err}`);
        }
    }

    console.log(`sectors to choose: ${JSON.stringify(sectorsToChooseFrom)}`);

    const removeField = (field: string) => {
        const fieldGroup = allowedFields.find(f => f.names.includes(field));
        setFields(fields.filter(f => !fieldGroup?.names.includes(f)));
    }

    const editedItems = selectAllItems ? items.filter(i => !excludedItems.some(ei => ei.cat === i.cat)).map(i => i as Partial<Item>) : selectedItems;
    console.log(`editing items: ${JSON.stringify(editedItems)}`);

    const maintenanceMethodsToChooseFrom = Object.values(MaintenanceMethod);

    return showLockError ? (<dialog open>
        <p>פריטים אלה פתוחים לעריכה על ידי משתמש אחר. אנא נסה שוב מאוחר יותר.</p>
        <BigButton text="חזרה" action={() => { navigate(-1); setShowLockError(false); }} />
    </dialog>) : (
        <div className={`${classes.itemMenu} ${classes.multiItemMenu}`}>
            <h1 className={classes.title}>עריכת פריטים</h1>
            <div className={classes.fields}>
                <LabeledInput label="שדה"  placeholder="שדה"
                    customInputElement={
                        <div className={classes.fieldSelectionContainer}>
                            <select className={classes.fieldSelection} name="fields" id="fields" onChange={e => {
                                // setSelectedField(e.target.value)
                                const selectedField = e.target.value;
                                setFields([ ...fields, ...(allowedFields.find(f => f.text ===  selectedField as string)?.names as string[])])
                            }
                            } value={""}>
                                {allowedFields
                                    .filter(({ names, exceptCatTypes }) => { 
                                        console.log(`exceptCatTypes: ${exceptCatTypes}, selectedItems: ${JSON.stringify(selectedItems)}`);
                                        return (!exceptCatTypes ||
                                        editedItems.every(selectedItem => !exceptCatTypes.includes(selectedItem.catType!))) &&
                                        !fields?.some(name => names.includes(name))
                                    })
                                    .map(({ text }) => <option>{text}</option>)}
                                <option value="" disabled selected>--- בחר שדה ---</option>
                            </select>
                            {/* {selectedField ?
                            <MdAddCircle onClick={() => { if (selectedField) { setFields([ ...fields, allowedFields.find(f => f.text ===  selectedField as string)?.name as string ]); setSelectedField('');} } }/> : <></>} */}
                        </div>}
                />
            </div>
            <div className={classes.details}>
                <ItemDetails
                    name=''
                    cat=''
                    kitCats={[]}
                    sector={sector}
                    department={department}
                    description=''
                    catType={CatType.Device}
                    emergency={emergency}
                    sectorsToChooseFrom={sectorsToChooseFrom}
                    handleInput={handleInput}
                    handleSetSector={handleSetSector}
                    handleSetDepartment={handleSetDepartment}
                    setEmergency={setEmergency}
                    fields={fields}
                    elementWrapper={(child, field) => (<span className={classes.removableField}>
                        <MdRemoveCircle onClick={() => removeField(field)}/>
                        {child}
                    </span>)}
                />
                <DeviceFields
                    cat=''
                    imageLink=''
                    qaStandardLink=''
                    medicalEngineeringManualLink=''
                    userManualLink=''
                    serviceManualLink=''
                    hebrewManualLink=''
                    schemasLink=''
                    supplier={supplier}
                    maintenanceMethod={maintenanceMethod}
                    maintenanceMethodsToChooseFrom={maintenanceMethodsToChooseFrom}
                    maintenanceIntervalMonths={maintenanceIntervalMonths}                    
                    storageTemperature={storageTemperature}
                    storageHumidity={storageHumidity}
                    models={[]}
                    accessories={[]}
                    consumables={[]}
                    spareParts={[]}
                    handleInput={handleInput}
                    setSupplier={setSupplier}
                    handleSetMaintenanceMethod={setMaintenanceMethod}
                    handleSetMaintenanceIntervalMonths={setMaintenanceIntervalMonths}  
                    handleSetStorageTemperature={setStorageTemperature}
                    handleSetStorageHumidity={setStorageHumidity}
                    fields={fields}
                    elementWrapper={(child, field) => (<span className={classes.removableField}>
                        <MdRemoveCircle onClick={() => removeField(field)}/>
                        {child}
                    </span>)}                    
                />
                <AccessoryFields
                    imageLink=''
                    userManualLink=''
                    schemasLink=''
                    supplier={supplier}
                    models={[]}
                    belongsToDevices={belongsToDevices}
                    handleInput={handleInput}
                    setSupplier={setSupplier}
                    setBelongsToDevices={setBelongsToDevices}
                    fields={fields.filter(f => f !== 'supplier')}
                    elementWrapper={(child, field) => (<span className={classes.removableField}>
                        <MdRemoveCircle onClick={() => removeField(field)}/>
                        {child}
                    </span>)}                     
                />
                <ConsumableFields
                    imageLink=''
                    userManualLink=''
                    schemasLink=''
                    supplier={supplier}
                    models={[]}
                    lifeSpan=''
                    belongsToDevices={belongsToDevices}
                    handleInput={handleInput}
                    setSupplier={setSupplier}
                    setBelongsToDevices={setBelongsToDevices}
                    fields={fields.filter(f => !['belongsToDevices', 'supplier'].includes(f))}
                    elementWrapper={(child, field) => (<span className={classes.removableField}>
                        <MdRemoveCircle onClick={() => removeField(field)}/>
                        {child}
                    </span>)}                     
                />
                <SparePartFields
                    imageLink=''
                    userManualLink=''
                    schemasLink=''
                    supplier={supplier}
                    models={[]}
                    belongsToDevices={belongsToDevices}
                    handleInput={handleInput}
                    setSupplier={setSupplier}
                    setBelongsToDevices={setBelongsToDevices}
                    fields={fields.filter(f => !['belongsToDevices', 'supplier'].includes(f))}
                    elementWrapper={(child, field) => (<span className={classes.removableField}>
                        <MdRemoveCircle onClick={() => removeField(field)}/>
                        {child}
                    </span>)}                     
                />
            </div>
            <div className={classes.buttons}>
                <BigButton text="שמור" action={handleSave} overrideStyle={{ marginTop: "2.5rem" }} className={classes.button} />
            </div>
            <Toaster />
        </div>
    )
};

export default MultiItemEdit;